import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetTracingContactAttemptsByPatientQueryKey,
  useCreateContactTracingContactAttemptByPatient,
} from '../../../../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import { useGetPatientByIdSuspense } from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { getCurrentDate, getCurrentTime } from '../../../../../utils/date.ts';

interface FormValues {
  date: string;
  time: string;
  note: string;
}

export function AddContactAttemptDialog({
  patientId,
  open,
  onClose,
}: {
  patientId: string;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: patient } = useGetPatientByIdSuspense(patientId);

  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      date: getCurrentDate(),
      time: getCurrentTime(),
      note: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const createContactAttemptMutation = useCreateContactTracingContactAttemptByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetTracingContactAttemptsByPatientQueryKey(patientId),
        });
        handleOnClose();
      },
      onError: () => toast.error('Beim Erfassen des Kontaktversuchs ist ein Fehler aufgetreten'),
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        date: getCurrentDate(),
        time: getCurrentTime(),
        note: '',
      });
    }
  }, [open]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 700 }}>
        <ModalClose />
        <Typography level={'h4'}>
          Kontaktversuch hinzufügen ({patient.lastName} {patient.firstName})
        </Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) =>
            createContactAttemptMutation.mutate({
              patientId: patientId,
              data: {
                datetime: data.date + 'T' + data.time,
                note: data.note,
              },
            }),
          )}>
          <Stack gap={2}>
            <FormGrid>
              <FormControl error={!!errors.date}>
                <FormLabel>
                  <Typography>Datum *</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="date"
                  rules={{
                    required: true,
                  }}
                />
                {errors.date && <FormHelperText>Datum muss ausgefüllt werden</FormHelperText>}
              </FormControl>
              <FormControl error={!!errors.time}>
                <FormLabel>
                  <Typography>Zeit *</Typography>
                </FormLabel>
                <Input {...register('time', { required: true })} type={'time'} />
                {errors.time && <FormHelperText>Zeit muss ausgefüllt werden</FormHelperText>}
              </FormControl>
            </FormGrid>

            <FormControl error={!!errors.note}>
              <FormLabel>
                <Typography>Notiz *</Typography>
              </FormLabel>
              <Input {...register('note', { required: true })} />
              {errors.note && <FormHelperText>Notiz muss ausgefüllt werden</FormHelperText>}
            </FormControl>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={createContactAttemptMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
